import axios from 'axios';

import { getAccessToken, redirectToLogin } from './auth.js';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach Bearer token from in-memory store
apiClient.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add X-Dev-User-Sub header when not in production auth mode
    try {
      const enableAuth = import.meta.env.VITE_ENABLE_AUTH;
      if (enableAuth !== 'true' && typeof localStorage !== 'undefined') {
        const devSub = localStorage.getItem('qms_dev_user_sub');
        if (devSub) config.headers['X-Dev-User-Sub'] = devSub;
      }
    } catch (err) {
      // Silently ignore localStorage access errors in development
      console.warn('Could not read dev user sub:', err.message);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling common responses and errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with error status
      switch (error.response.status) {
        case 401:
          console.warn('Unauthorized — redirecting to login');
          redirectToLogin();
          break;
        case 403:
          // Handle forbidden
          console.warn('Forbidden access');
          break;
        case 404:
          console.warn('Resource not found');
          break;
        case 500:
          console.error('Server error');
          break;
        default:
          console.error('API Error:', error.response.data);
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('Network error - no response received');
    } else {
      // Something happened in setting up the request
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

// API service functions
export const apiService = {
  // Health check
  health: () => apiClient.get('/health'),

  // Templates API
  templates: {
    // Get all templates with optional query parameters
    getAll: (params = {}) => {
      const { type, isDefault, sortBy, sortDirection, page, limit } = params;
      const queryParams = new URLSearchParams();
      if (type) queryParams.append('type', type);
      if (isDefault !== undefined)
        queryParams.append('isDefault', String(isDefault));
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortDirection) queryParams.append('sortDirection', sortDirection);
      if (page) queryParams.append('page', page);
      if (limit) queryParams.append('limit', limit);

      const query = queryParams.toString();
      return apiClient.get(`/templates${query ? `?${query}` : ''}`);
    },

    // Get template by ID
    getById: (id) => apiClient.get(`/templates/${id}`),

    // Create new template
    create: (data) => apiClient.post('/templates', data),

    // Update template
    update: (id, data) => apiClient.put(`/templates/${id}`, data),

    // Delete template
    delete: (id) => apiClient.delete(`/templates/${id}`),

    // Set template as default
    setDefault: (id) => apiClient.put(`/templates/${id}/default`),

    // Duplicate template
    duplicate: (id, creator = 'Cliff') =>
      apiClient.post(`/templates/${id}/duplicate`, { creator }),
  },

  // Users API (for future use)
  users: {
    getAll: () => apiClient.get('/users'),
    getById: (id) => apiClient.get(`/users/${id}`),
    create: (data) => apiClient.post('/users', data),
    update: (id, data) => apiClient.put(`/users/${id}`, data),
    delete: (id) => apiClient.delete(`/users/${id}`),
    resetPassword: (id, newPassword) =>
      apiClient.post(`/users/${id}/password/reset`, { newPassword }),
    changeRole: (id, groups) => apiClient.put(`/users/${id}/role`, { groups }),
    updateName: (id, name, designation, department) =>
      apiClient.put(`/users/${id}/attributes`, {
        name,
        designation,
        department,
      }),
    createCognitoUser: (data) => apiClient.post('/users/cognito', data),
    disableUser: (id) => apiClient.post(`/users/${id}/disable`),
    enableUser: (id) => apiClient.post(`/users/${id}/enable`),
    getCognitoUsers: () => apiClient.get('/users/cognito'),
    refreshCache: () => apiClient.post('/users/cache/refresh'),
  },

  // Comments API
  comments: {
    getAll: ({ entityType, entityId, entityRevision }) => {
      const params = new URLSearchParams({ entityType, entityId });
      if (entityRevision !== undefined)
        params.append('entityRevision', entityRevision);
      return apiClient.get(`/comments?${params}`);
    },
    create: (data) => apiClient.post('/comments', data),
    update: (id, data) => apiClient.put(`/comments/${id}`, data),
    delete: (id) => apiClient.delete(`/comments/${id}`),
    bulkFreeze: (data) => apiClient.patch('/comments/bulk-freeze', data),
  },

  // Documents API
  documents: {
    // Get all documents with optional query parameters
    getAll: (params = {}) => {
      const { sortBy, sortDirection, page, limit, status, unlinked } = params;
      const queryParams = new URLSearchParams();
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortDirection) queryParams.append('sortDirection', sortDirection);
      if (page) queryParams.append('page', page);
      if (limit) queryParams.append('limit', limit);
      if (status) queryParams.append('status', status);
      if (unlinked) queryParams.append('unlinked', unlinked);

      const query = queryParams.toString();
      return apiClient.get(`/documents${query ? `?${query}` : ''}`);
    },

    // Get document by ID
    getById: (id) => apiClient.get(`/documents/${id}`),

    // Create new document
    create: (data) => apiClient.post('/documents', data),

    // Update document
    update: (id, data) => apiClient.put(`/documents/${id}`, data),

    // Delete document
    delete: (id) => apiClient.delete(`/documents/${id}`),

    // Lifecycle endpoints
    submitNew: (body) => apiClient.post('/documents/submit', body),
    submit: (id, body) => apiClient.put(`/documents/${id}/submit`, body),
    approve: (id, body) => apiClient.post(`/documents/${id}/approve`, body),
    reject: (id, body) => apiClient.post(`/documents/${id}/reject`, body),
    returnToRequestor: (id, body) =>
      apiClient.post(`/documents/${id}/return`, body),
    release: (id, body) => apiClient.post(`/documents/${id}/release`, body),
    archive: (id) => apiClient.post(`/documents/${id}/archive`),
    revise: (id, body) => apiClient.post(`/documents/${id}/revise`, body),
    getRevisions: (id) => apiClient.get(`/documents/${id}/revisions`),

    // PDF-related endpoints
    getPDFUrl: (id) => apiClient.get(`/documents/${id}/pdf`),
    regeneratePDF: (id) => apiClient.post(`/documents/${id}/regenerate-pdf`),
  },

  // Controls API
  controls: {
    // CRUD
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.documentId)
        queryParams.append('documentId', params.documentId);
      if (params.isLinked !== undefined)
        queryParams.append('isLinked', params.isLinked);
      const query = queryParams.toString();
      return apiClient.get(`/controls${query ? `?${query}` : ''}`);
    },
    getById: (id) => apiClient.get(`/controls/${id}`),
    create: (data) => apiClient.post('/controls', data),
    update: (id, data) => apiClient.put(`/controls/${id}`, data),
    delete: (id) => apiClient.delete(`/controls/${id}`),

    // Acknowledgment (R&U)
    acknowledge: (id) => apiClient.post(`/controls/${id}/ack`),
    getAckStatus: (id) => apiClient.get(`/controls/${id}/ack/status`),

    // Lifecycle endpoints
    submitNew: (data) => apiClient.post('/controls/submit', data),
    submit: (id, data) => apiClient.put(`/controls/${id}/submit`, data),
    approve: (id, data) => apiClient.post(`/controls/${id}/approve`, data),
    completeTraining: (id) => apiClient.post(`/controls/${id}/complete-training`),
    reject: (id, data) => apiClient.post(`/controls/${id}/reject`, data),
    return: (id, data) => apiClient.post(`/controls/${id}/return`, data),
    release: (id, data) => apiClient.post(`/controls/${id}/release`, data),
  },

  // Quality Events API
  qualities: {
    // CRUD
    list: (params = {}) => {
      const { status, page, limit, sortBy, sortDirection } = params;
      const queryParams = new URLSearchParams();
      if (status) queryParams.append('status', status);
      if (page) queryParams.append('page', page);
      if (limit) queryParams.append('limit', limit);
      if (sortBy) queryParams.append('sortBy', sortBy);
      if (sortDirection) queryParams.append('sortDirection', sortDirection);
      const query = queryParams.toString();
      return apiClient.get(`/qualities${query ? `?${query}` : ''}`);
    },
    get: (id) => apiClient.get(`/qualities/${id}`),
    create: (data) => apiClient.post('/qualities', data),
    update: (id, data) => apiClient.put(`/qualities/${id}`, data),
    updateCategories: (id, categories) =>
      apiClient.patch(`/qualities/${id}/categories`, { categories }),
    delete: (id) => apiClient.delete(`/qualities/${id}`),

    // Lifecycle endpoints
    submit: (id, body) => apiClient.post(`/qualities/${id}/submit`, body),
    approve: (id, body) => apiClient.post(`/qualities/${id}/approve`, body),
    reject: (id, body) => apiClient.post(`/qualities/${id}/reject`, body),
    returnQe: (id, body) => apiClient.post(`/qualities/${id}/return`, body),
    release: (id) => apiClient.post(`/qualities/${id}/release`),
    checkActionPlans: (id, body) =>
      apiClient.post(`/qualities/${id}/check-action-plans`, body),
    checkEffectiveness: (id, body) =>
      apiClient.post(`/qualities/${id}/check-effectiveness`, body),
    linkDocument: (id, body) =>
      apiClient.post(`/qualities/${id}/link-document`, body),
  },

  uploads: (data) =>
    apiClient.post('/uploads/presign', {
      fileName: data.name,
      fileSize: data.size,
      contentType: data.type,
    }),
  uploadFile: async (file) => {
    const response = await apiClient.post('/uploads/presign', {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
    });
    const upload = response.data?.data;

    if (!response.data?.success || !upload?.url)
      throw new Error(response.data?.error || 'Failed to prepare file upload');

    const uploadResponse = await fetch(upload.url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'x-amz-meta-originalname': file.name,
      },
      body: file,
    });

    if (!uploadResponse.ok) throw new Error(`Failed to upload ${file.name}`);

    return {
      key: upload.key,
      originalName: upload.originalName || file.name,
      contentType: upload.contentType || file.type,
      size: file.size,
    };
  },
};

export { apiClient };
export default apiClient;
